import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import { contactInfo } from '../data/mock';

// WhatsApp SVG Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`, '_blank');
  };

  const scrollToSection = (id) => {
    // Eğer ana sayfada değilsek, önce ana sayfaya git
    if (location.pathname !== '/') {
      navigate('/');
      // Ana sayfaya gittikten sonra scroll yapmak için timeout kullan
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const offset = 100;
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementPosition - offset,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // Ana sayfadaysak direkt scroll yap
      const element = document.getElementById(id);
      if (element) {
        const offset = 100;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - offset,
          behavior: 'smooth'
        });
      }
    }
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { id: 'hero', label: 'Ana Sayfa' },
    { id: 'services', label: 'Hizmetler' },
    { id: 'about', label: 'Hakkımızda' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Neon Glow Background */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-2xl"></div>
      <div className="absolute inset-0 border-b-2 border-neon-blue/30" style={{
        boxShadow: '0 0 20px rgba(0, 212, 255, 0.3), 0 10px 40px rgba(0, 212, 255, 0.1)'
      }}></div>
      
      {/* Animated Neon Lines in Header */}
      <div className="absolute inset-0 overflow-hidden opacity-30">
        <div className="absolute w-full h-px bg-gradient-to-r from-transparent via-neon-blue to-transparent top-0 animate-pulse"></div>
        <div className="absolute w-full h-px bg-gradient-to-r from-transparent via-parliament-blue to-transparent bottom-0" style={{
          animation: 'shine 3s infinite'
        }}></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Orijinal Boyut, Çerçeve Kaldırıldı, Boy Sığdırıldı */}
          <button onClick={() => navigate('/')} className="flex items-center transition-all duration-300 hover:opacity-80">
            <img 
              src="https://customer-assets.emergentagent.com/job_cdclean/artifacts/fd0170b0_Ads%C4%B1z%20tasar%C4%B1m%20%2813%29.png"
              alt="C&D Clean Logo"
              className="h-full w-auto object-contain"
              style={{
                height: '70px',
                maxHeight: '70px'
              }}
            />
          </button>

          {/* Desktop Navigation - Daha Büyük Font */}
          <nav className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-base font-bold transition-all duration-300 relative group px-4 py-2 text-gray-300 hover:text-neon-blue"
              >
                <span className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-parliament-blue via-neon-blue to-parliament-blue w-0 group-hover:w-full transition-all duration-300" style={{
                  boxShadow: '0 0 10px rgba(0, 212, 255, 0.8)'
                }}></span>
                <span className="absolute inset-0 bg-neon-blue/0 group-hover:bg-neon-blue/10 rounded-lg transition-all duration-300"></span>
                <span className="relative z-10">{link.label}</span>
              </button>
            ))}
          </nav>

          {/* WhatsApp Button - Desktop & Mobile */}
          <div className="flex items-center space-x-2">
            <Button
              onClick={handleWhatsApp}
              className="bg-gradient-to-r from-green-500 via-green-600 to-green-500 hover:from-green-600 hover:to-green-700 text-white font-bold text-sm px-6 py-4 md:px-6 md:py-5 transition-all duration-300 relative overflow-hidden group"
              style={{
                boxShadow: '0 0 20px rgba(34, 197, 94, 0.5), 0 0 40px rgba(34, 197, 94, 0.3)'
              }}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-green-600 via-green-500 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              <span className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></span>
              <WhatsAppIcon className="w-5 h-5 mr-2 relative z-10" />
              <span className="relative z-10">WhatsApp</span>
            </Button>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-3 rounded-lg border-2 border-neon-blue/30 hover:border-neon-blue transition-all"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                boxShadow: '0 0 15px rgba(0, 212, 255, 0.3)'
              }}
            >
              {mobileMenuOpen ? 
                <X className="w-6 h-6 text-neon-blue" /> : 
                <Menu className="w-6 h-6 text-neon-blue" />
              }
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 animate-fade-in">
            <nav className="flex flex-col space-y-3">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="text-base font-bold transition-all px-4 py-3 rounded-lg border border-neon-blue/20 text-gray-300 hover:text-neon-blue hover:bg-neon-blue/5 text-left"
                >
                  {link.label}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;